import { Box, Button, TextField, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";

import { monospaceStyle } from "../../theme";
import { formatFilterCount } from "./FilterRow";

// One numeric key a range can be set for
export interface NumericRangeSpec {
  key: string;
  label: string;
  // The unit its values, and the bounds of a range, are in
  unit: string;
  // How many state the key; shown as "stated by N"
  count: number;
  // The lowest and the highest value stated, shown as hints in the fields
  minimum?: number | null;
  maximum?: number | null;
}

// The range set for one key. Null for no bound on that side.
export interface NumericRange {
  key: string;
  minimum: number | null;
  maximum: number | null;
}

// Keys shown before the rest is asked for
export const NUMERIC_RANGE_LIMIT = 8;

export interface NumericRangeFilterProps {
  // The keys, in the order to show them
  specs: NumericRangeSpec[];
  // The ranges that are set, one per key at most
  ranges: readonly NumericRange[];
  // Called with the whole range of a key when one of its bounds changed.
  // A range with neither bound means the key's range was emptied.
  onChange: (range: NumericRange) => void;
  // Keys shown before the rest is asked for
  limit?: number;
}

const boundText = (bound: number | null | undefined): string =>
  bound === null || bound === undefined ? "" : String(bound);

interface BoundFieldProps {
  // The field's name, e.g. "Minimum Weight"
  name: string;
  // The bound that is set
  value: number | null;
  // Shown while the field is empty
  hint: string;
  onCommit: (bound: number | null) => void;
}

// One bound. Typing changes nothing; leaving the field or pressing Enter
// sets the bound. Text that is no number is put back.
const BoundField: React.FC<BoundFieldProps> = ({
  name,
  value,
  hint,
  onCommit,
}) => {
  const [draft, setDraft] = useState(boundText(value));

  // The bound changes without the field when its owner's state does
  useEffect(() => {
    setDraft(boundText(value));
  }, [value]);

  const commit = () => {
    const text = draft.trim().replace(",", ".");
    const bound = text === "" ? null : Number(text);
    if (bound !== null && !Number.isFinite(bound)) {
      setDraft(boundText(value));
      return;
    }
    if (bound !== value) {
      onCommit(bound);
    } else {
      setDraft(boundText(value));
    }
  };

  return (
    <TextField
      size="small"
      value={draft}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          commit();
        }
      }}
      placeholder={hint}
      inputProps={{ "aria-label": name, inputMode: "decimal" }}
      sx={{
        flex: 1,
        minWidth: 0,
        "& .MuiOutlinedInput-input": {
          ...monospaceStyle,
          fontSize: "0.75rem",
          py: 0.5,
          px: 1,
        },
      }}
    />
  );
};

// Ranges of numeric keys: per key its label, its unit, how many state it,
// and a minimum and a maximum field. The first keys are shown, the rest on
// request; a key with a range stays in view.
const NumericRangeFilter: React.FC<NumericRangeFilterProps> = ({
  specs,
  ranges,
  onChange,
  limit = NUMERIC_RANGE_LIMIT,
}) => {
  const [showAll, setShowAll] = useState(false);

  const rangeOf = (key: string): NumericRange =>
    ranges.find((range) => range.key === key) ?? {
      key,
      minimum: null,
      maximum: null,
    };

  const visible = showAll
    ? specs
    : specs.filter(
        (spec, index) =>
          index < limit || ranges.some((range) => range.key === spec.key),
      );

  if (specs.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary">
        No values
      </Typography>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {visible.map((spec) => {
        const range = rangeOf(spec.key);
        return (
          <Box key={spec.key} role="group" aria-label={spec.label}>
            <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75 }}>
              <Typography
                component="span"
                variant="body2"
                sx={{ fontSize: "0.8125rem", overflowWrap: "anywhere" }}
              >
                {spec.label}
              </Typography>
              <Typography
                component="span"
                variant="caption"
                color="text.secondary"
                sx={monospaceStyle}
              >
                {spec.unit}
              </Typography>
              <Box sx={{ flex: 1 }} />
              <Typography
                component="span"
                variant="caption"
                color="text.secondary"
                sx={{ ...monospaceStyle, flexShrink: 0 }}
              >
                {`stated by ${formatFilterCount(spec.count)}`}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 0.75, mt: 0.5 }}>
              <BoundField
                name={`Minimum ${spec.label}`}
                value={range.minimum}
                hint={`min ${boundText(spec.minimum)}`.trim()}
                onCommit={(minimum) => onChange({ ...range, minimum })}
              />
              <BoundField
                name={`Maximum ${spec.label}`}
                value={range.maximum}
                hint={`max ${boundText(spec.maximum)}`.trim()}
                onCommit={(maximum) => onChange({ ...range, maximum })}
              />
            </Box>
          </Box>
        );
      })}

      {visible.length < specs.length && (
        <Button
          variant="text"
          size="small"
          onClick={() => setShowAll(true)}
          sx={{ textTransform: "none", alignSelf: "flex-start", py: 0 }}
        >
          {`Show all ${specs.length}`}
        </Button>
      )}
    </Box>
  );
};

export default NumericRangeFilter;
